import React, { useEffect, useState } from "react";
import { AlertTriangle, ArmchairIcon, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { t, tf } from "@/i18n";

/** One dialog for the whole board: keyboard-friendly alternative to dragging. */
export const MoveGuestDialog = ({ guest, plan, avoidMap, guestMap, onMove, onClose }) => {
  const partySize = guest?.party_size || 1;
  const [withParty, setWithParty] = useState(partySize > 1);

  useEffect(() => {
    setWithParty((guest?.party_size || 1) > 1);
  }, [guest?.id, guest?.party_size]);

  if (!guest) return null;
  const avoids = avoidMap?.[guest.id] || [];
  const move = (targetTable) => onMove(guest.id, targetTable, withParty && partySize > 1);

  return (
    <Dialog open={Boolean(guest)} onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent className="max-w-lg" data-testid="move-guest-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{tf("move.title", { name: guest.name })}</DialogTitle>
          <DialogDescription>
            {guest.table ? tf("move.currently", { t: guest.table }) : t("move.inTray")} {t("move.flagged")}
          </DialogDescription>
        </DialogHeader>
        <div className="scroll-thin max-h-[340px] space-y-1.5 overflow-y-auto pr-1">
          {(plan?.tables || []).map((table) => {
            const clash = avoids
              .filter((id) => (table.guest_ids || []).includes(id))
              .map((id) => guestMap[id]?.name || id);
            const free = table.capacity - table.seats_used;
            const current = guest.table === table.table;
            return (
              <button
                key={table.table}
                type="button"
                data-testid={`move-to-table-${table.table}`}
                disabled={current}
                onClick={() => move(table.table)}
                className="flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left disabled:opacity-60"
                style={{
                  borderColor: clash.length ? "var(--danger)" : "var(--line)",
                  backgroundColor: current ? "var(--paper-2)" : "#ffffff",
                }}
              >
                <span className="min-w-0">
                  <span className="font-num text-[10px] tracking-[0.14em] text-[color:var(--gild)]">
                    {tf("table.label", { n: table.table })}
                  </span>
                  <span className="block truncate text-[13.5px] font-medium">{table.title}</span>
                  {clash.length ? (
                    <span className="mt-0.5 flex items-center gap-1 text-[11.5px] font-semibold" style={{ color: "var(--danger)" }}>
                      <AlertTriangle className="h-3 w-3" /> {tf("move.wouldBeWith", { names: clash.join(", ") })}
                    </span>
                  ) : null}
                </span>
                <span className="font-num shrink-0 text-[11.5px] text-[color:var(--ink-soft)]">
                  {current ? (
                    <span className="flex items-center gap-1">
                      <Check className="h-3.5 w-3.5" /> {t("move.here")}
                    </span>
                  ) : (
                    tf("move.free", { n: free })
                  )}
                </span>
              </button>
            );
          })}
        </div>
        {partySize > 1 ? (
          <label
            className="flex items-center gap-2 rounded-lg surface-2 px-2.5 py-2 text-[12.5px] text-[color:var(--ink-soft)]"
            data-testid="move-with-party-label"
          >
            <input
              type="checkbox"
              checked={withParty}
              onChange={(event) => setWithParty(event.target.checked)}
              data-testid="move-with-party-checkbox"
            />
            {tf("move.wholeParty", {
              n: partySize,
              label: guest.party_label ? ` — ${guest.party_label}` : "",
            })}
          </label>
        ) : null}

        <div className="flex items-center justify-between gap-2 border-t border-[color:var(--line)] pt-3">
          <Button variant="outline" data-testid="move-to-tray-button" onClick={() => move(null)} disabled={!guest.table}>
            <ArmchairIcon className="mr-2 h-4 w-4" /> {t("move.setAside")}
          </Button>
          <Button variant="ghost" onClick={onClose} data-testid="move-dialog-close">
            {t("move.cancel")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MoveGuestDialog;
